import { FC, useCallback, useMemo, useState } from "react";

import { useAppSelector } from "../../store";

import { useNotifications } from "../../app/useNotifications";
import { Communication } from "./communication";
import { RestrictedSiteCustomField } from "../types";
import { responseNotification } from "../../components/Notifications";
import { FieldsGroupWrapper } from "../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverFieldsGroups/FieldsGroupWrapper";
import { getFieldsProps } from "../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverFieldsGroups/helpers/getFieldsProps";
import { SiteCustomFieldTypeEnum } from "@vviinn/user-managment-client-ts/dist/models/SiteCustomField";
import { RenderFieldProps } from "../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverFieldsGroups/types";

const EMPTY_FIELD: RestrictedSiteCustomField = { type: "string", name: "" };

const fieldTypes: SiteCustomFieldTypeEnum[] = [
  "string",
  "boolean",
  "float",
  "enumerated",
  "tree",
  "name",
  "identifier",
];

const REQUIRED_NON_EMPTY_KEYS: (keyof RestrictedSiteCustomField)[] = ["name"];

// Lowercase letters, numbers and underscore only; a native pattern must match the whole value.
const CUSTOM_FIELD_NAME_PATTERN = "[a-z0-9_]+";

const fieldRenderProps: RenderFieldProps<keyof RestrictedSiteCustomField>[] = [
  {
    key: "name",
    pattern: CUSTOM_FIELD_NAME_PATTERN,
    placeholder: "custom_field_123",
  },
  {
    key: "type",
    options: fieldTypes,
    title: "Field Type",
  },
];

export const CustomSiteFields: FC = () => {
  const [currentFields, setCurrentFields] = useState<
    RestrictedSiteCustomField[]
  >([]);

  const { notify } = useNotifications();
  const { upsertCustomFieldsData } = Communication();

  const customFields = useAppSelector((state) => state.site.customFields);
  const siteId = useAppSelector((state) => state.site.id);
  const companyId = useAppSelector((state) => state.company.company?.id);
  const defaultFilters = useAppSelector((state) => state.campaigns.filters);

  const initConfig = useCallback(() => {
    setCurrentFields(customFields ?? []);
  }, [customFields]);

  const {
    handleRemove,
    handleAdd,
    fieldsRenderData,
    filterOutEmptyFields,
    areSameFields,
  } = useMemo(
    () =>
      getFieldsProps<RestrictedSiteCustomField>(
        setCurrentFields,
        EMPTY_FIELD,
        REQUIRED_NON_EMPTY_KEYS,
      ),
    [],
  );

  const acceptFieldsChange = useCallback(async () => {
    if (!siteId || !companyId) return false;
    notify("Info", "Updating site custom fields");
    const response = await upsertCustomFieldsData(
      siteId,
      companyId,
      filterOutEmptyFields(currentFields),
    );
    return responseNotification(notify, !!response, "Site custom fields");
  }, [
    currentFields,
    siteId,
    companyId,
    notify,
    upsertCustomFieldsData,
    filterOutEmptyFields,
  ]);

  const renderFields = useMemo(
    () => fieldsRenderData(currentFields, fieldRenderProps),
    [currentFields, fieldsRenderData],
  );

  const allowSubmit = useMemo(
    () => !areSameFields(customFields, currentFields),
    [customFields, currentFields, areSameFields],
  );

  return (
    <FieldsGroupWrapper
      fieldsType="field"
      initFieldsCount={customFields.length}
      initConfig={initConfig}
      addFieldHandler={handleAdd}
      removeFieldHandler={handleRemove}
      submitFieldHandler={acceptFieldsChange}
      fieldRender={renderFields}
      allowSubmit={allowSubmit}
      importantFields={REQUIRED_NON_EMPTY_KEYS}
      builtInValues={defaultFilters}
      blockSubmitOnDuplicates={true}
    />
  );
};
